import { DatePipe } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { VoucherModel } from '@app/core/models/voucher/voucher.model';
import { VoucherService } from '@app/core/services';
import { ProductService } from '@app/shared/services/product.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ClientService } from '@app/core/services';

@Component({
  selector: 'app-ticket-item-box',
  templateUrl: './ticket-item-box.component.html',
  styleUrls: ['./ticket-item-box.component.scss']
})
export class TicketItemBoxComponent implements OnInit, OnDestroy {

  private _ticket: VoucherModel;
  public eventDate: string;
  private destroy$: Subject<void> = new Subject<void>();
  
  @Input() 
  set ticket(value: VoucherModel) {
    this._ticket = value;
    this.formatEventDate();
  }
  
  get ticket(): VoucherModel {
    return this._ticket;
  }
  
  constructor(
    private productService: ProductService, 
    private voucherService: VoucherService,
    private clientService: ClientService,
    private datePipe: DatePipe
  ) {}
  
  ngOnInit(): void {
     this.clientService.clientSubject
        .pipe(takeUntil(this.destroy$))
        .subscribe((data) => {
          if (data != null) {
            this.customizeThemeColor(data.theme.ThemeColor);
          }
        });  
  }


  /**
   * Apply dynamic theme color to ticket elements
   * @param colour The theme color from client settings
   */
  customizeThemeColor(colour: string) {
    if (!colour) return;
    
    // Get the ticket header and apply the theme color gradient
    const ticketHeader = document.querySelector('.ticket-header') as HTMLElement;
    if (ticketHeader) {
      ticketHeader.style.background = `linear-gradient(135deg, ${this.adjustBrightness(colour, 20)} 0%, ${colour} 100%)`;
    }
    
    // Apply theme color to download button
    const downloadButton = document.querySelector('.download-button') as HTMLElement;
    if (downloadButton) {
      downloadButton.style.backgroundColor = colour;
    }
    
    // Apply theme color to QR code border
    const qrContainer = document.querySelector('.qr-container') as HTMLElement;
    if (qrContainer && qrContainer.style) {
      const beforeStyle = document.createElement('style');
      beforeStyle.textContent = `.qr-container:before { border-color: ${colour} !important; }`;
      document.head.appendChild(beforeStyle);
    }
  }
  
  /**
   * Adjust color brightness
   * @param color Hex color code
   * @param percent Percentage to lighten (positive) or darken (negative)
   * @returns Modified hex color
   */
  private adjustBrightness(color: string, percent: number): string {
    // Remove # if present
    color = color.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);
    
    // Adjust brightness
    const adjustR = Math.min(255, Math.max(0, Math.round(r + (percent / 100) * 255)));
    const adjustG = Math.min(255, Math.max(0, Math.round(g + (percent / 100) * 255)));
    const adjustB = Math.min(255, Math.max(0, Math.round(b + (percent / 100) * 255)));
    
    // Convert back to hex
    return '#' + 
      ((1 << 24) + (adjustR << 16) + (adjustG << 8) + adjustB)
        .toString(16)
        .slice(1);
  }
  
  /**
   * Format the event date from the ticket information
   * Uses validPeriodStart from the voucher model
   * Converts UTC date to local timezone
   */
  private formatEventDate(): void {
    if (this.ticket && this.ticket.validPeriodStart) {
      try {
        // Get the date string from the ticket
        const dateStr = String(this.ticket.validPeriodStart);
        
        // Create a date object with explicit UTC handling
        let date: Date;
        
        // Check if the string already has timezone info
        if (dateStr.endsWith('Z') || dateStr.indexOf('+') > -1) {
          // Already has timezone info
          date = new Date(dateStr);
        } else {
          // Assume UTC if no timezone specified
          date = new Date(dateStr + 'Z'); // Append Z to indicate UTC
        }
        
        // Format the date for display
        this.eventDate = this.datePipe.transform(date, 'MMM d, y, h:mm a');
      } catch (error) {
        console.error('Error formatting date:', error);
        this.eventDate = null;
      }
    } else {
      this.eventDate = null;
    }
  }
  
  /**
   * Clean up subscriptions when component is destroyed
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDownloadTicket(){
    this.voucherService.getTicket(this.ticket.voucherId)
     .subscribe({
      next:(blob: Blob): void => {
        const file = new Blob([blob], {type: 'application/pdf'});
        const fileURL = URL.createObjectURL(file);
        
        // Open the PDF in a new window with specific dimensions
        // The URL format will be like: blob:http://localhost:4220/ed598c62-2d3b-455c-b179-b892f98ff687
        const newWindow = window.open('', '_blank', 'width=1000, height=800');
        if (newWindow) {
          newWindow.location.href = fileURL;
        }
      },
       error: (errorResponse: any)=>{
        console.error('Error downloading ticket:', errorResponse);
       }
      })
   }

}
